import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, shouldRenderMobile, ToastService } from '@philips-emr/hpms-provider-commons';
import { HpmsTextHtmlAPI } from '@philips-emr/hpms-provider-components';
import { ActionAPI, DeckAPI, FetchAPI, FieldAPI, FormAPI, PanelAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color, ToastLevel } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnClick, OnClose, OnDynamicContentRender, OnMount, OnSelectItem, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, RenderEvent, SelectItemEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService, SessionStorageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { FragmentNames } from '../../codegen';
import { TechnicalVisitAppointmentsAliases as Aliases } from '../../codegen/constants/aliases/technical-visit-appointments-aliases';
import BaseController from '../BaseController';
import './technical-visit-controller.css';

interface Appointment {
  ieStatus: AppointmentStatus,
  dsStatus: string,
  nrSeqAppointment: number,
  history: string,
  message: string,
  suggestedDate: string,
  refusalReason: string,
}

enum AppointmentStatus {
  DO_NOT_APPLY = '0',
  PENDING = '1',
  CONFIRMED = '2',
  REFUSED = '3',
  CANCELED = '4',
  ONGOING = '5',
  CONCLUDED = '6',
}

@Controller({ fragment: FragmentNames.TECHNICAL_VISIT_APPOINTMENTS })
export default class TechnicalVisitAppointmentsController extends BaseController {

  @Inject(Aliases.FETCH_APPOINTMENTS)
  private fetchAppointments: FetchAPI;

  @Inject(Aliases.HPMS_TEXT_HTML)
  private hpmsTextHtml: HpmsTextHtmlAPI;

  @Inject(Aliases.ACTION_REFUSE)
  private actionRefuse: ActionAPI;

  @Inject(Aliases.ACTION_CONFIRM)
  private actionConfirm: ActionAPI;

  @Inject(Aliases.PANEL_MODAL)
  private panelModal: PanelAPI;

  @Inject(Aliases.FORM_DATA)
  private formData: FormAPI;

  @Inject(Aliases.DECK_HISTORIES)
  private deckHistories: DeckAPI;

  @Inject(Aliases.FETCH_HISTORIES)
  private fetchHistories: FetchAPI;

  @Inject(Aliases.FORM_MENU_MOBILE)
  private formMenuMobile: FormAPI;

  @Inject(Aliases.FIELD_REFUSAL_REASON)
  private fieldRefusalReason: FieldAPI;

  @OnMount([Aliases.FETCH_HISTORIES, Aliases.FETCH_MENUS_MOBILE])
  onMountDataFetch(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqVisit });
    api.forceFetch();
  }

  @OnChange(Aliases.AUTOCOMPLETE_MENU_MOBILE)
  onChange(event: ChangeEvent<{ nrSeqAppointment: string }>): void {
    const nrSeqAppointment = event.value.nrSeqAppointment;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    this.fetchAppointments.setParameters({ nrSeqVisit, nrSeqAppointment });
    this.fetchAppointments.forceFetch();
  }

  @OnDynamicContentRender(Aliases.CARD_HISTORIES)
  onRenderAppointmentsHistory({ data, render }: RenderEvent): void {
    const { ieStatus } = data as unknown as Appointment;
    const stampForm = this.getStampAppointments(ieStatus);
    if (stampForm) {
      render((): ReactElement => stampForm);
    }
  }

  @OnAfterFetch([Aliases.FETCH_HISTORIES, Aliases.FETCH_MENUS_MOBILE])
  onAfterFetchHistories(event: FetchEvent): void {
    if (!event.data) return;

    const data = event.data as Appointment[];
    let nrSeqAppointment = SessionStorageService.getItem('nrSeqAppointment');
    SessionStorageService.removeItem('nrSeqAppointment');
    const { nrSeqAppointment: nrSequence } = data[0];
    if (!nrSeqAppointment) {
      nrSeqAppointment = nrSequence;
    }

    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    this.fetchAppointments.setParameters({ nrSeqVisit, nrSeqAppointment });
    this.fetchAppointments.forceFetch();
    this.deckHistories.selectCard(String(nrSeqAppointment));
  }

  @OnSelectItem(Aliases.DECK_HISTORIES)
  onClickAppointment(event: SelectItemEvent<any>): void {
    const { nrSeqAppointment } = event.item as Appointment;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    this.fetchAppointments.setParameters({ nrSeqVisit, nrSeqAppointment });
    this.fetchAppointments.forceFetch();
  }

  @OnAfterFetch(Aliases.FETCH_APPOINTMENTS)
  onMountTermoTexto(event: FetchEvent): void {
    if (!event.data) return;

    const { history, ieStatus, suggestedDate, nrSeqAppointment, refusalReason } = event.data as Appointment;

    this.fieldRefusalReason.setVisible(!isEmptyArrayOrObject(refusalReason));

    this.actionRefuse.setEnabled(ieStatus == AppointmentStatus.PENDING);
    this.actionConfirm.setEnabled(ieStatus == AppointmentStatus.PENDING);
    this.hpmsTextHtml.setData(history);
    this.formMenuMobile.setData({ menuMobile: { suggestedDate, nrSeqAppointment } });

  }

  @OnSubmit(Aliases.FORM_MESSAGE)
  onSubmitReject(event: SubmitEvent): void {
    const nrSeqAppointment = this.context.get('nrSeqAppointment');
    const { message } = event.record as Appointment;
    HttpService.post(Endpoint.POST.REJECT_APPOINTMENT, { nrSeqAppointment, message })
      .then(() => {
        this.actionRefuse.setEnabled(false);
        this.actionConfirm.setEnabled(false);
        ToastService.open(
          {
            level: ToastLevel.SUCCESS,
            message: MessageService.translate('provider.reject-appointment-success-message'),
          });
        this.panelModal.setVisible(false);
        this.fetchHistories.forceFetch();
      }).catch(() => ToastService.open({ level: ToastLevel.ERROR, message: MessageService.translate('provider.error-process-request') }));
  }

  @OnClick(Aliases.ACTION_CONFIRM)
  onClickConfirm(): void {
    const { nrSeqAppointment } = this.formData.getData() as unknown as Appointment;
    this.confirmAppointment(nrSeqAppointment);
  }

  @OnClick(Aliases.ACTION_REFUSE)
  onClickRefuse(): void {
    const { nrSeqAppointment } = this.formData.getData() as unknown as Appointment;
    this.refuseAppointment(nrSeqAppointment);
  }

  @OnClose(Aliases.MODAL_REFUSE)
  onCloseModal(): void {
    this.panelModal.setVisible(false);
  }

  @OnCancel(Aliases.FORM_MESSAGE)
  onCancelForm(): void {
    this.panelModal.setVisible(false);
  }

  @OnMount(Aliases.PANEL_MOBILE)
  onMountPanelMobile(event: MountEvent): void {
    const api = event.target as PanelAPI;
    api.setVisible(shouldRenderMobile());
  }

  private confirmAppointment(nrSeqAppointment: number): void {
    void HttpService.post(Endpoint.POST.CONFIRM_APPOINTMENT, { nrSeqAppointment }).then(() => {
      this.actionRefuse.setEnabled(false);
      this.actionConfirm.setEnabled(false);
      this.fetchHistories.forceFetch();
      ToastService.open({ level: ToastLevel.SUCCESS, message: MessageService.translate('provider.confirm-appointment-sucess-message') });
    }).catch(() => ToastService.open({ level: ToastLevel.ERROR, message: MessageService.translate('provider.error-process-request') }));
  }

  private refuseAppointment(nrSeqAppointment: number): void {
    this.panelModal.setVisible(true);
    this.context.set({ nrSeqAppointment });
  }

  private getStampAppointments(ieStatus: AppointmentStatus): ReactElement | null {
    let stampProps = null;
    switch (ieStatus) {
      case AppointmentStatus.PENDING:
        stampProps = { color: Color.SAFETY_YELLOW, icon: StampIcon.WARNING, label: '' };
        break;
      case AppointmentStatus.CONFIRMED:
        stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CHECK, label: '' };
        break;
      case AppointmentStatus.REFUSED:
        stampProps = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label: '' };
        break;
      case AppointmentStatus.CANCELED:
        stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED_OUTLINE, label: '' };
        break;
      case AppointmentStatus.ONGOING:
        stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CLOCK, label: '' };
        break;
      case AppointmentStatus.CONCLUDED:
        stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label: '' };
        break;
      default:
        return null;
    }
    return <Stamp {...stampProps} />;
  }
}
